import { getLocale } from '../i18n';
import { supabase } from '../supabaseClient';
import { resolveSiteTimeZone } from '../utils/siteScheduling';

const PAGE_SIZE = 1000;
const CHUNK_SIZE = 200;
const LIVE_TRACK_WINDOW_MINUTES = 15;
const RECENT_TIMELINE_DAYS = 2;
const ANALYTICS_CACHE_TTL_MS = 60_000;

const dashboardCache = new Map<string, {
  expiresAt: number;
  dashboard: AnalyticsDashboard;
}>();

type MaybeSingle<T> = T | T[] | null | undefined;
type TimelineKind = 'execution' | 'segment' | 'checkpoint' | 'report' | 'alarm' | 'departure' | 'intervention';

const DUTY_KIND_LABELS: Record<string, string> = {
  fixed_post: 'Fixed Post',
  patrol: 'Patrol',
  desk: 'Desk',
  mobile: 'Mobile',
  custom: 'Custom',
};

const REPORT_STATUS_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  in_review: 'In Review',
  needs_more_info: 'Needs Info',
  escalated: 'Escalated',
};

export type RiskTone = 'stable' | 'watch' | 'high' | 'critical';
export type TimelineTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export interface AnalyticsHeadline {
  plannedDutyHours: number;
  staffedDutyHours: number;
  executedHours: number;
  duePublishedShifts: number;
  startedShifts: number;
  lateStarts: number;
  unstartedPublishedShifts: number;
  completedDutySegments: number;
  missedDutySegments: number;
  skippedDutySegments: number;
  upcomingOpenPositions: number;
  checkpointCompleted: number;
  checkpointPlanned: number;
  flaggedLocations: number;
}

export interface AnalyticsQuality {
  fillRate: number | null;
  publicationRate: number | null;
  startRate: number | null;
  punctualityRate: number | null;
  dutyCompletionRate: number | null;
  checkpointCompletionRate: number | null;
  geofenceCompliance: number | null;
  reportAssignmentRate: number | null;
}

export interface AnalyticsCapacityPoint {
  key: string;
  shortLabel: string;
  fullLabel: string;
  plannedHours: number;
  staffedHours: number;
  executedHours: number;
  publishedHours: number;
  openPositions: number;
}

export interface AnalyticsStartPoint {
  key: string;
  shortLabel: string;
  fullLabel: string;
  onTimeStarts: number;
  lateStarts: number;
  unstartedShifts: number;
}

export interface AnalyticsCompletionPoint {
  key: string;
  shortLabel: string;
  fullLabel: string;
  completedSegments: number;
  missedSegments: number;
  skippedSegments: number;
}

export interface AnalyticsDutyMixItem {
  key: string;
  label: string;
  plannedHours: number;
  staffedHours: number;
  slotCount: number;
  fillRate: number | null;
}

export interface AnalyticsHeatmapCell {
  key: string;
  label: string;
  hours: number;
  intensity: number;
}

export interface AnalyticsHeatmapRow {
  key: string;
  label: string;
  cells: AnalyticsHeatmapCell[];
}

export interface AnalyticsHeatmapChart {
  columns: string[];
  rows: AnalyticsHeatmapRow[];
  maxHours: number;
}

export interface AnalyticsIncidentMixItem {
  key: string;
  label: string;
  count: number;
  tone: TimelineTone;
}

export interface AnalyticsIncidentSummary {
  openReports: number;
  submittedReports: number;
  inReviewReports: number;
  needsMoreInfoReports: number;
  escalatedReports: number;
  activeAlarms: number;
  averageOpenReportAgeHours: number | null;
  averageReportCloseHours: number | null;
  averageAlarmResolveMinutes: number | null;
  statusMix: AnalyticsIncidentMixItem[];
}

export interface AnalyticsLiveSummary {
  activeExecutions: number;
  activeSites: number;
  overrunningExecutions: number;
  liveTrackedGuards: number;
  lowBatteryCount: number;
  activeSiteDepartures: number;
  manualInterventions: number;
}

export interface AnalyticsSiteRiskRow {
  siteId: string | null;
  siteName: string;
  plannedHours: number;
  executedHours: number;
  upcomingFillRate: number | null;
  publicationRate: number | null;
  upcomingOpenPositions: number;
  lateStarts: number;
  missedSegments: number;
  openReports: number;
  activeAlarms: number;
  flaggedLocations: number;
  manualInterventions: number;
  activeExecutions: number;
  riskScore: number;
  riskLabel: string;
  riskTone: RiskTone;
}

export interface AnalyticsGuardLeader {
  guardId: string;
  fullName: string;
  plannedHours: number;
  executedHours: number;
  punctualityRate: number | null;
  dutyCompletionRate: number | null;
  checkpointCompletionRate: number | null;
  lateStarts: number;
  missedSegments: number;
  siteCount: number;
  liveTracked: boolean;
  activeExecution: boolean;
  momentumScore: number;
}

export interface AnalyticsUpcomingGap {
  dutyId: string;
  siteId: string | null;
  siteName: string;
  dutyName: string;
  dutyKind: string;
  slotDate: string;
  /** Calendar date on which the duty begins when minutes extend beyond midnight. */
  occurrenceDate?: string;
  startMinute: number;
  endMinute: number;
  guardsRequired: number;
  assignedCount: number;
  openPositions: number;
  isPublished: boolean;
  requiredLicenceTypeId: string | null;
}

export interface AnalyticsTimelineItem {
  id: string;
  occurredAt: string;
  kind: TimelineKind;
  tone: TimelineTone;
  title: string;
  detail: string;
  siteName: string | null;
  guardName: string | null;
}

export interface AnalyticsSiteOption {
  id: string;
  name: string;
}

export interface AnalyticsDashboard {
  companyName: string;
  generatedAt: string;
  rangeDays: number;
  rangeLabel: string;
  selectedSiteId: string | null;
  selectedSiteName: string | null;
  availableSites: AnalyticsSiteOption[];
  warnings: string[];
  headline: AnalyticsHeadline;
  quality: AnalyticsQuality;
  incidents: AnalyticsIncidentSummary;
  live: AnalyticsLiveSummary;
  charts: {
    capacity: AnalyticsCapacityPoint[];
    starts: AnalyticsStartPoint[];
    completions: AnalyticsCompletionPoint[];
    dutyMix: AnalyticsDutyMixItem[];
    heatmap: AnalyticsHeatmapChart;
  };
  riskBoard: AnalyticsSiteRiskRow[];
  guardLeaders: AnalyticsGuardLeader[];
  upcomingGaps: AnalyticsUpcomingGap[];
  timeline: AnalyticsTimelineItem[];
}

interface CompanyRow {
  id: string;
  name: string | null;
}

interface ProfileRow {
  id: string;
  company_id: string | null;
  role: string;
  /** Holds a guard role, whatever else they hold. */
  is_guard: boolean;
  full_name: string | null;
  status: string | null;
}

interface SiteRow {
  id: string;
  name: string | null;
  country_code: string | null;
  timezone: string | null;
}

interface DutySlotAssignmentRow {
  id: string;
  guard_id: string;
  assigned_at: string | null;
  guard?: MaybeSingle<{ full_name: string | null }>;
}

interface DutySlotRow {
  id: string;
  company_id: string;
  site_id: string;
  slot_date: string;
  name: string;
  duty_kind: string;
  colour: string | null;
  start_minute: number;
  end_minute: number;
  guards_required: number;
  is_published: boolean;
  display_order: number;
  required_licence_type_id: string | null;
  assignments?: DutySlotAssignmentRow[] | null;
}

interface ShiftRow {
  id: string;
  shift_key: string;
  site_id: string;
  guard_id: string;
  slot_date: string;
  start_minute: number;
  end_minute: number;
  is_published: boolean;
}

interface ShiftExecutionRow {
  id: string;
  shift_id: string | null;
  site_id: string | null;
  guard_id: string | null;
  planned_shift_key: string;
  planned_slot_date: string;
  planned_start_at: string | null;
  planned_end_at: string | null;
  clock_in_late_by_seconds: number | null;
  actual_clock_in_at: string | null;
  actual_clock_out_at: string | null;
  status: string | null;
}

interface ShiftExecutionSegmentRow {
  id: string;
  shift_execution_id: string;
  site_id: string;
  guard_id: string;
  planned_segment_type: string;
  planned_label: string;
  planned_duty_kind: string | null;
  planned_start_at: string;
  planned_end_at: string;
  source_slot_id: string;
  patrol_id: string | null;
  post_id: string | null;
  status: string;
  actual_started_at: string | null;
  actual_ended_at: string | null;
}

interface CheckpointCompletionRow {
  id: string;
  shift_execution_id: string;
  shift_execution_segment_id: string | null;
  site_id: string;
  guard_id: string;
  patrol_id: string | null;
  status: string;
  recorded_at: string;
  completed_late_by_seconds: number | null;
}

interface SiteDepartureRow {
  id: string;
  shift_execution_id: string;
  shift_execution_segment_id: string | null;
  site_id: string;
  guard_id: string;
  departure_type: string;
  departed_at: string;
  returned_at: string | null;
}

interface ManualInterventionRow {
  id: string;
  shift_execution_id: string;
  shift_execution_segment_id: string | null;
  site_id: string;
  guard_id: string;
  intervention_type: string;
  summary: string;
  intervention_at: string;
}

interface LocationSummaryRow {
  site_id: string | null;
  geofence_samples: number;
  within_geofence_samples: number;
  flagged_locations: number;
}

interface PatrolCheckpointRow {
  id: string;
  patrol_id: string;
  sequence_order: number;
  is_required: boolean;
}

interface ReportRow {
  id: string;
  site_id: string | null;
  guard_id: string | null;
  created_at: string;
  content: string | null;
  report_type: string;
  priority: string | null;
  status: string;
  status_updated_at: string | null;
  assigned_to: string | null;
}

interface AlarmRow {
  id: string;
  site_id: string | null;
  triggered_by: string | null;
  title: string;
  status: string;
  triggered_at: string;
  resolved_at: string | null;
}

interface GuardLiveLocationRow {
  guard_id: string;
  updated_at: string;
  recorded_at: string;
  battery: number | null;
  shift_execution_id: string | null;
  shift_execution_segment_id: string | null;
}

interface SiteAccumulator {
  siteId: string | null;
  siteName: string;
  plannedHours: number;
  executedHours: number;
  upcomingRequiredPositions: number;
  upcomingAssignedPositions: number;
  upcomingPublishedPositions: number;
  upcomingOpenPositions: number;
  lateStarts: number;
  missedSegments: number;
  openReports: number;
  activeAlarms: number;
  flaggedLocations: number;
  manualInterventions: number;
  activeExecutions: number;
}

interface GuardAccumulator {
  guardId: string;
  fullName: string;
  plannedHours: number;
  executedHours: number;
  dueShifts: number;
  startedShifts: number;
  lateStarts: number;
  completedSegments: number;
  missedSegments: number;
  skippedSegments: number;
  checkpointCompleted: number;
  checkpointPlanned: number;
  siteIds: Set<string>;
  liveTracked: boolean;
  activeExecution: boolean;
}

interface DayDescriptor {
  key: string;
  shortLabel: string;
  fullLabel: string;
}

function startOfDay(date: Date): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function toDate(value: unknown): Date | null {
  if (!value) return null;
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDateKey(value: string): Date {
  const [year, month, day] = value.split('-').map((part) => Number.parseInt(part, 10));
  return new Date(year, month - 1, day);
}

function dateKeyToSerial(value: string): number {
  const [year, month, day] = value.split('-').map((part) => Number.parseInt(part, 10));
  return Math.floor(Date.UTC(year, month - 1, day) / 86400000);
}

function diffDateKeys(left: string, right: string): number {
  return dateKeyToSerial(right) - dateKeyToSerial(left);
}

function minutesBetween(startValue: unknown, endValue: unknown): number {
  const start = toDate(startValue);
  const end = toDate(endValue);
  if (!start || !end) return 0;
  return Math.max((end.getTime() - start.getTime()) / 60000, 0);
}

function hoursBetween(startValue: unknown, endValue: unknown): number {
  return minutesBetween(startValue, endValue) / 60;
}

function minutesSince(value: unknown, now: Date): number {
  const date = toDate(value);
  if (!date) return Number.POSITIVE_INFINITY;
  return Math.max((now.getTime() - date.getTime()) / 60000, 0);
}

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

function average(values: number[]): number | null {
  if (!values.length) return null;
  return sum(values) / values.length;
}

function percentage(part: number, total: number): number | null {
  if (!total) return null;
  return (part / total) * 100;
}

function chunk<T>(values: T[], size: number): T[][] {
  const groups: T[][] = [];
  for (let index = 0; index < values.length; index += size) {
    groups.push(values.slice(index, index + size));
  }
  return groups;
}

async function fetchAllRows<T>(
  queryFactory: (from: number, to: number) => Promise<{ data: T[] | null; error: unknown }>,
): Promise<T[]> {
  let from = 0;
  const rows: T[] = [];

  while (true) {
    const to = from + PAGE_SIZE - 1;
    const { data, error } = await queryFactory(from, to);
    if (error) throw error;

    const batch = data ?? [];
    rows.push(...batch);
    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return rows;
}

async function fetchRowsByChunks<T>(
  values: string[],
  queryFactory: (chunkValues: string[], from: number, to: number) => Promise<{ data: T[] | null; error: unknown }>,
): Promise<T[]> {
  if (!values.length) return [];
  const batches = await Promise.all(
    chunk(values, CHUNK_SIZE).map((group) => fetchAllRows((from, to) => queryFactory(group, from, to))),
  );
  return batches.flat();
}

function withOptionalSiteFilter(
  query: any,
  siteId: string | null | undefined,
): any {
  return siteId ? query.eq('site_id', siteId) : query;
}

function dedupeById<T extends { id: string }>(rows: T[]): T[] {
  const map = new Map<string, T>();
  rows.forEach((row) => {
    if (!map.has(row.id)) map.set(row.id, row);
  });
  return [...map.values()];
}

function getDutyDurationHours(slot: Pick<DutySlotRow, 'start_minute' | 'end_minute'>): number {
  return Math.max(slot.end_minute - slot.start_minute, 0) / 60;
}

function getShiftDurationHours(shift: Pick<ShiftRow, 'start_minute' | 'end_minute'>): number {
  return Math.max(shift.end_minute - shift.start_minute, 0) / 60;
}

function getAssignedCount(slot: DutySlotRow): number {
  return Math.min(slot.assignments?.length ?? 0, slot.guards_required);
}

function getOpenPositions(slot: DutySlotRow): number {
  return Math.max(slot.guards_required - getAssignedCount(slot), 0);
}

function getExecutionWorkedHours(execution: ShiftExecutionRow, now: Date): number {
  if (!execution.actual_clock_in_at) return 0;
  if (execution.actual_clock_out_at) {
    return hoursBetween(execution.actual_clock_in_at, execution.actual_clock_out_at);
  }
  if (execution.status === 'active') {
    return hoursBetween(execution.actual_clock_in_at, now.toISOString());
  }
  return 0;
}

function getShortLabel(date: Date, rangeDays: number): string {
  if (rangeDays <= 14) {
    return date.toLocaleDateString(getLocale(), { weekday: 'short' });
  }
  return date.toLocaleDateString(getLocale(), { month: 'short', day: 'numeric' });
}

function buildDayDescriptors(rangeStart: Date, rangeDays: number): DayDescriptor[] {
  return Array.from({ length: rangeDays }, (_, offset) => {
    const date = addDays(rangeStart, offset);
    return {
      key: formatDateKey(date),
      shortLabel: getShortLabel(date, rangeDays),
      fullLabel: date.toLocaleDateString(getLocale(), { weekday: 'short', month: 'short', day: 'numeric' }),
    };
  });
}

function createSiteAccumulator(siteId: string | null, siteName: string): SiteAccumulator {
  return {
    siteId,
    siteName,
    plannedHours: 0,
    executedHours: 0,
    upcomingRequiredPositions: 0,
    upcomingAssignedPositions: 0,
    upcomingPublishedPositions: 0,
    upcomingOpenPositions: 0,
    lateStarts: 0,
    missedSegments: 0,
    openReports: 0,
    activeAlarms: 0,
    flaggedLocations: 0,
    manualInterventions: 0,
    activeExecutions: 0,
  };
}

function ensureSiteAccumulator(
  map: Map<string, SiteAccumulator>,
  siteId: string | null | undefined,
  siteMap: Map<string, SiteRow>,
): SiteAccumulator {
  const key = siteId ?? 'unassigned';
  if (!map.has(key)) {
    const siteName = siteId ? (siteMap.get(siteId)?.name?.trim() || 'Unknown site') : 'Unassigned site';
    map.set(key, createSiteAccumulator(siteId ?? null, siteName));
  }
  return map.get(key)!;
}

function createGuardAccumulator(guardId: string, fullName: string): GuardAccumulator {
  return {
    guardId,
    fullName,
    plannedHours: 0,
    executedHours: 0,
    dueShifts: 0,
    startedShifts: 0,
    lateStarts: 0,
    completedSegments: 0,
    missedSegments: 0,
    skippedSegments: 0,
    checkpointCompleted: 0,
    checkpointPlanned: 0,
    siteIds: new Set<string>(),
    liveTracked: false,
    activeExecution: false,
  };
}

function ensureGuardAccumulator(
  map: Map<string, GuardAccumulator>,
  guardId: string | null | undefined,
  profileMap: Map<string, ProfileRow>,
): GuardAccumulator | null {
  if (!guardId) return null;
  if (!map.has(guardId)) {
    const fullName = profileMap.get(guardId)?.full_name?.trim() || 'Unknown guard';
    map.set(guardId, createGuardAccumulator(guardId, fullName));
  }
  return map.get(guardId)!;
}

function getRiskTone(score: number): RiskTone {
  if (score >= 18) return 'critical';
  if (score >= 10) return 'high';
  if (score >= 4) return 'watch';
  return 'stable';
}

function getRiskLabel(score: number): string {
  if (score >= 18) return 'Critical';
  if (score >= 10) return 'High';
  if (score >= 4) return 'Watch';
  return 'Stable';
}

function humanizeKey(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function getDutyKindLabel(value: string): string {
  return DUTY_KIND_LABELS[value] ?? humanizeKey(value);
}

function getSiteName(siteId: string | null | undefined, siteMap: Map<string, SiteRow>): string {
  if (!siteId) return 'Unassigned site';
  return siteMap.get(siteId)?.name?.trim() || 'Unknown site';
}

function getGuardName(guardId: string | null | undefined, profileMap: Map<string, ProfileRow>): string {
  if (!guardId) return 'Unknown guard';
  return profileMap.get(guardId)?.full_name?.trim() || 'Unknown guard';
}

function getReportTitle(report: Pick<ReportRow, 'content' | 'report_type'>): string {
  const content = report.content?.trim();
  if (content) {
    const words = content.split(/\s+/).slice(0, 7).join(' ');
    return content.split(/\s+/).length > 7 ? `${words}...` : words;
  }
  return humanizeKey(report.report_type || 'general');
}

function getRangeLabel(rangeDays: number): string {
  if (rangeDays === 7) return 'Last 7 days';
  if (rangeDays === 30) return 'Last 30 days';
  return 'Last 90 days';
}

function getZonedNowState(now: Date, timeZone: string): { dateKey: string; minuteOfDay: number } {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const parts = Object.fromEntries(
    formatter
      .formatToParts(now)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  );

  const hours = Number(parts.hour ?? '0') % 24;
  const minutes = Number(parts.minute ?? '0');

  return {
    dateKey: `${parts.year}-${parts.month}-${parts.day}`,
    minuteOfDay: (hours * 60) + minutes,
  };
}

function isShiftDue(shift: ShiftRow, site: SiteRow | undefined, now: Date): boolean {
  const timeZone = resolveSiteTimeZone(site ?? null);
  const current = getZonedNowState(now, timeZone);
  const dayDelta = diffDateKeys(shift.slot_date, current.dateKey);

  if (dayDelta < 0) return false;
  if (dayDelta === 0) return shift.end_minute <= current.minuteOfDay;
  if (dayDelta === 1) return shift.end_minute <= (1440 + current.minuteOfDay);
  return true;
}

async function getCurrentProfile(): Promise<{ id: string; company_id: string; role: string }> {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('profiles')
    .select('id, company_id, role')
    .eq('id', user.id)
    .single();

  if (error) throw error;
  if (!data?.company_id) throw new Error('This account is not attached to a company.');

  return data as { id: string; company_id: string; role: string };
}

export const analyticsService = {
  async fetchDashboard(options: {
    rangeDays?: number;
    siteId?: string | null;
    cacheBust?: number;
  } = {}): Promise<AnalyticsDashboard> {
    const { rangeDays = 30, siteId: selectedSiteId = null, cacheBust = 0 } = options;
    const profile = await getCurrentProfile();
    const companyId = profile.company_id;
    const cacheKey = `${companyId}:${rangeDays}:${selectedSiteId ?? 'all'}:${cacheBust}`;
    const cached = dashboardCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) return cached.dashboard;
    const now = new Date();
    const today = startOfDay(now);
    const tomorrow = addDays(today, 1);
    const rangeStart = addDays(today, -(rangeDays - 1));
    const futureHorizonEnd = addDays(today, 14);
    const recentTimelineStart = addDays(today, -RECENT_TIMELINE_DAYS);
    const liveTrackStart = new Date(now.getTime() - (LIVE_TRACK_WINDOW_MINUTES * 60 * 1000));

    const rangeStartDate = formatDateKey(rangeStart);
    const todayDate = formatDateKey(today);
    const futureEndDate = formatDateKey(futureHorizonEnd);

    const [
      company,
      profiles,
      sites,
      dutySlots,
      shifts,
      executions,
      segments,
      checkpointRows,
      departureRows,
      interventionRows,
      locationSummaryRows,
      reportsInRange,
      openReportsRaw,
      alarmsInRange,
      activeAlarmsRaw,
    ] = await Promise.all([
      supabase
        .from('companies')
        .select('id, name')
        .eq('id', companyId)
        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return data as CompanyRow;
        }),
      fetchAllRows<ProfileRow>((from, to) => supabase
        .from('profiles')
        .select('id, company_id, role, is_guard, full_name, status')
        .eq('company_id', companyId)
        .order('full_name', { ascending: true })
        .range(from, to) as unknown as Promise<{ data: ProfileRow[] | null; error: unknown }>),
      fetchAllRows<SiteRow>((from, to) => supabase
        .from('sites')
        .select('id, name, country_code, timezone')
        .eq('company_id', companyId)
        .order('name', { ascending: true })
        .range(from, to) as unknown as Promise<{ data: SiteRow[] | null; error: unknown }>),
      fetchAllRows<DutySlotRow>((from, to) => withOptionalSiteFilter(
        supabase
          .from('duty_slots')
          .select(`
          id,
          company_id,
          site_id,
          slot_date,
          name,
          duty_kind,
          colour,
          start_minute,
          end_minute,
          guards_required,
          is_published,
          display_order,
          required_licence_type_id,
          assignments:duty_slot_assignments(
            id,
            guard_id,
            assigned_at,
            guard:profiles!duty_slot_assignments_guard_id_fkey(full_name)
          )
        `)
        .eq('company_id', companyId)
        .gte('slot_date', rangeStartDate)
        .lte('slot_date', futureEndDate),
        selectedSiteId,
      )
        .order('slot_date', { ascending: true })
        .order('start_minute', { ascending: true })
        .range(from, to) as unknown as Promise<{ data: DutySlotRow[] | null; error: unknown }>),
      fetchAllRows<ShiftRow>((from, to) => withOptionalSiteFilter(
        supabase
          .from('shifts')
          .select('id, shift_key, site_id, guard_id, slot_date, start_minute, end_minute, is_published')
          .eq('company_id', companyId)
          .gte('slot_date', rangeStartDate)
          .lte('slot_date', todayDate),
        selectedSiteId,
      )
        .order('slot_date', { ascending: true })
        .order('start_minute', { ascending: true })
        .range(from, to) as unknown as Promise<{ data: ShiftRow[] | null; error: unknown }>),
      fetchAllRows<ShiftExecutionRow>((from, to) => withOptionalSiteFilter(
        supabase
          .from('shift_executions')
          .select(`
          id,
          shift_id,
          site_id,
          guard_id,
          planned_shift_key,
          planned_slot_date,
          planned_start_at,
          planned_end_at,
          clock_in_late_by_seconds,
          actual_clock_in_at,
          actual_clock_out_at,
          status
        `)
        .eq('company_id', companyId)
        .gte('planned_slot_date', rangeStartDate)
        .lte('planned_slot_date', todayDate),
        selectedSiteId,
      )
        .order('planned_start_at', { ascending: true })
        .range(from, to) as unknown as Promise<{ data: ShiftExecutionRow[] | null; error: unknown }>),
      fetchAllRows<ShiftExecutionSegmentRow>((from, to) => withOptionalSiteFilter(
        supabase
          .from('shift_execution_segments')
          .select(`
          id,
          shift_execution_id,
          site_id,
          guard_id,
          planned_segment_type,
          planned_label,
          planned_duty_kind,
          planned_start_at,
          planned_end_at,
          source_slot_id,
          patrol_id,
          post_id,
          status,
          actual_started_at,
          actual_ended_at
        `)
        .eq('company_id', companyId)
        .gte('planned_start_at', rangeStart.toISOString())
        .lt('planned_start_at', tomorrow.toISOString()),
        selectedSiteId,
      )
        .order('planned_start_at', { ascending: true })
        .range(from, to) as unknown as Promise<{ data: ShiftExecutionSegmentRow[] | null; error: unknown }>),
      fetchAllRows<CheckpointCompletionRow>((from, to) => withOptionalSiteFilter(
        supabase
          .from('shift_execution_checkpoint_completions')
          .select(`
          id,
          shift_execution_id,
          shift_execution_segment_id,
          site_id,
          guard_id,
          patrol_id,
          status,
          recorded_at,
          completed_late_by_seconds
        `)
        .eq('company_id', companyId)
        .gte('recorded_at', rangeStart.toISOString())
        .lt('recorded_at', tomorrow.toISOString()),
        selectedSiteId,
      )
        .order('recorded_at', { ascending: true })
        .range(from, to) as unknown as Promise<{ data: CheckpointCompletionRow[] | null; error: unknown }>),
      fetchAllRows<SiteDepartureRow>((from, to) => withOptionalSiteFilter(
        supabase
          .from('shift_execution_site_departures')
          .select(`
          id,
          shift_execution_id,
          shift_execution_segment_id,
          site_id,
          guard_id,
          departure_type,
          departed_at,
          returned_at
        `)
        .eq('company_id', companyId)
        .gte('departed_at', rangeStart.toISOString())
        .lt('departed_at', tomorrow.toISOString()),
        selectedSiteId,
      )
        .order('departed_at', { ascending: true })
        .range(from, to) as unknown as Promise<{ data: SiteDepartureRow[] | null; error: unknown }>),
      fetchAllRows<ManualInterventionRow>((from, to) => withOptionalSiteFilter(
        supabase
          .from('shift_execution_manual_interventions')
          .select(`
          id,
          shift_execution_id,
          shift_execution_segment_id,
          site_id,
          guard_id,
          intervention_type,
          summary,
          intervention_at
        `)
        .eq('company_id', companyId)
        .gte('intervention_at', rangeStart.toISOString())
        .lt('intervention_at', tomorrow.toISOString()),
        selectedSiteId,
      )
        .order('intervention_at', { ascending: true })
        .range(from, to) as unknown as Promise<{ data: ManualInterventionRow[] | null; error: unknown }>),
      supabase
        .rpc('get_analytics_location_summary', {
          p_company_id: companyId,
          p_range_start: rangeStart.toISOString(),
          p_range_end: tomorrow.toISOString(),
          p_site_id: selectedSiteId,
        })
        .then(({ data, error }) => {
          if (error) throw error;
          return (data ?? []) as LocationSummaryRow[];
        }),
      fetchAllRows<ReportRow>((from, to) => withOptionalSiteFilter(
        supabase
          .from('reports')
          .select(`
          id,
          site_id,
          guard_id,
          created_at,
          content,
          report_type,
          priority,
          status,
          status_updated_at,
          assigned_to
        `)
        .eq('company_id', companyId)
        .gte('created_at', rangeStart.toISOString())
        .lt('created_at', tomorrow.toISOString()),
        selectedSiteId,
      )
        .order('created_at', { ascending: false })
        .range(from, to) as unknown as Promise<{ data: ReportRow[] | null; error: unknown }>),
      fetchAllRows<ReportRow>((from, to) => withOptionalSiteFilter(
        supabase
          .from('reports')
          .select(`
          id,
          site_id,
          guard_id,
          created_at,
          content,
          report_type,
          priority,
          status,
          status_updated_at,
          assigned_to
        `)
        .eq('company_id', companyId)
        .neq('status', 'closed'),
        selectedSiteId,
      )
        .order('created_at', { ascending: false })
        .range(from, to) as unknown as Promise<{ data: ReportRow[] | null; error: unknown }>),
      fetchAllRows<AlarmRow>((from, to) => withOptionalSiteFilter(
        supabase
          .from('alarms')
          .select('id, site_id, triggered_by, title, status, triggered_at, resolved_at')
          .eq('company_id', companyId)
          .gte('triggered_at', rangeStart.toISOString())
          .lt('triggered_at', tomorrow.toISOString()),
        selectedSiteId,
      )
        .order('triggered_at', { ascending: false })
        .range(from, to) as unknown as Promise<{ data: AlarmRow[] | null; error: unknown }>),
      fetchAllRows<AlarmRow>((from, to) => withOptionalSiteFilter(
        supabase
          .from('alarms')
          .select('id, site_id, triggered_by, title, status, triggered_at, resolved_at')
          .eq('company_id', companyId)
          .in('status', ['open', 'acknowledged']),
        selectedSiteId,
      )
        .order('triggered_at', { ascending: false })
        .range(from, to) as unknown as Promise<{ data: AlarmRow[] | null; error: unknown }>),
    ]);

    const profileMap = new Map(profiles.map((row) => [row.id, row]));
    const siteMap = new Map(sites.map((row) => [row.id, row]));
    const availableSites = sites.map((site) => ({
      id: site.id,
      name: site.name?.trim() || 'Unknown site',
    }));
    const guards = profiles.filter((member) => member.is_guard);
    const guardIds = guards.map((guard) => guard.id);
    const patrolIds = [...new Set(
      segments
        .map((segment) => segment.patrol_id)
        .filter((patrolId): patrolId is string => Boolean(patrolId)),
    )];

    const [liveLocations, patrolCheckpoints] = await Promise.all([
      guardIds.length
        ? fetchRowsByChunks<GuardLiveLocationRow>(
          guardIds,
          (ids, from, to) => supabase
            .from('guard_live_locations')
            .select('guard_id, updated_at, recorded_at, battery, shift_execution_id, shift_execution_segment_id')
            .in('guard_id', ids)
            .gte('updated_at', liveTrackStart.toISOString())
            .order('updated_at', { ascending: false })
            .range(from, to) as unknown as Promise<{ data: GuardLiveLocationRow[] | null; error: unknown }>,
        )
        : Promise.resolve<GuardLiveLocationRow[]>([]),
      patrolIds.length
        ? fetchRowsByChunks<PatrolCheckpointRow>(
          patrolIds,
          (ids, from, to) => supabase
            .from('patrol_checkpoints')
            .select('id, patrol_id, sequence_order, is_required')
            .in('patrol_id', ids)
            .order('sequence_order', { ascending: true })
            .range(from, to) as unknown as Promise<{ data: PatrolCheckpointRow[] | null; error: unknown }>,
        )
        : Promise.resolve<PatrolCheckpointRow[]>([]),
    ]);

    const executionIds = new Set(executions.map((execution) => execution.id));
    const segmentIds = new Set(segments.map((segment) => segment.id));
    const scopedLiveLocations = selectedSiteId
      ? liveLocations.filter((row) => (
        (row.shift_execution_id ? executionIds.has(row.shift_execution_id) : false)
        || (row.shift_execution_segment_id ? segmentIds.has(row.shift_execution_segment_id) : false)
      ))
      : liveLocations;

    const reports = dedupeById([...reportsInRange, ...openReportsRaw]);
    const openReports = reports.filter((report) => report.status !== 'closed');
    const alarms = dedupeById([...alarmsInRange, ...activeAlarmsRaw]);
    const activeAlarms = alarms.filter((alarm) => alarm.status === 'open' || alarm.status === 'acknowledged');

    const rangeDutySlots = dutySlots.filter((slot) => slot.slot_date >= rangeStartDate && slot.slot_date <= todayDate);
    const futureDutySlots = dutySlots.filter((slot) => slot.slot_date >= todayDate && slot.slot_date <= futureEndDate);

    const descriptors = buildDayDescriptors(rangeStart, rangeDays);
    const capacitySeries: AnalyticsCapacityPoint[] = descriptors.map((descriptor) => ({
      ...descriptor,
      plannedHours: 0,
      staffedHours: 0,
      executedHours: 0,
      publishedHours: 0,
      openPositions: 0,
    }));
    const startsSeries: AnalyticsStartPoint[] = descriptors.map((descriptor) => ({
      ...descriptor,
      onTimeStarts: 0,
      lateStarts: 0,
      unstartedShifts: 0,
    }));
    const completionSeries: AnalyticsCompletionPoint[] = descriptors.map((descriptor) => ({
      ...descriptor,
      completedSegments: 0,
      missedSegments: 0,
      skippedSegments: 0,
    }));

    const capacityLookup = new Map(capacitySeries.map((point) => [point.key, point]));
    const startsLookup = new Map(startsSeries.map((point) => [point.key, point]));
    const completionLookup = new Map(completionSeries.map((point) => [point.key, point]));

    const checkpointRowsBySegmentId = new Map<string, CheckpointCompletionRow[]>();
    checkpointRows.forEach((row) => {
      if (!row.shift_execution_segment_id) return;
      if (!checkpointRowsBySegmentId.has(row.shift_execution_segment_id)) {
        checkpointRowsBySegmentId.set(row.shift_execution_segment_id, []);
      }
      checkpointRowsBySegmentId.get(row.shift_execution_segment_id)!.push(row);
    });

    const requiredCheckpointCountByPatrolId = new Map<string, number>();
    patrolCheckpoints
      .filter((checkpoint) => checkpoint.is_required !== false)
      .forEach((checkpoint) => {
        requiredCheckpointCountByPatrolId.set(
          checkpoint.patrol_id,
          (requiredCheckpointCountByPatrolId.get(checkpoint.patrol_id) ?? 0) + 1,
        );
      });

    const latestLiveByGuard = new Map<string, GuardLiveLocationRow>();
    scopedLiveLocations.forEach((row) => {
      const existing = latestLiveByGuard.get(row.guard_id);
      const rowDate = toDate(row.updated_at) ?? toDate(row.recorded_at);
      const existingDate = existing ? (toDate(existing.updated_at) ?? toDate(existing.recorded_at)) : null;
      if (!existing || (rowDate && existingDate && rowDate > existingDate)) {
        latestLiveByGuard.set(row.guard_id, row);
      }
    });

    const liveTrackedRows = [...latestLiveByGuard.values()].filter((row) => (
      minutesSince(row.updated_at || row.recorded_at, now) <= LIVE_TRACK_WINDOW_MINUTES
    ));
    const liveTrackedGuardIds = new Set(liveTrackedRows.map((row) => row.guard_id));
    const lowBatteryCount = liveTrackedRows.filter((row) => row.battery != null && row.battery < 20).length;

    const executionByShiftId = new Set(
      executions
        .map((execution) => execution.shift_id)
        .filter((shiftId): shiftId is string => Boolean(shiftId)),
    );
    const executionByShiftKey = new Set(executions.map((execution) => execution.planned_shift_key).filter(Boolean));

    const duePublishedShifts = shifts.filter((shift) => (
      shift.is_published
      && isShiftDue(shift, siteMap.get(shift.site_id), now)
    ));
    const startedDueShifts = duePublishedShifts.filter((shift) => (
      executionByShiftId.has(shift.id) || executionByShiftKey.has(shift.shift_key)
    ));
    const unstartedDueShifts = duePublishedShifts.filter((shift) => (
      !executionByShiftId.has(shift.id) && !executionByShiftKey.has(shift.shift_key)
    ));

    rangeDutySlots.forEach((slot) => {
      const point = capacityLookup.get(slot.slot_date);
      if (!point) return;
      const durationHours = getDutyDurationHours(slot);
      const assignedCount = getAssignedCount(slot);
      point.plannedHours += durationHours * slot.guards_required;
      point.staffedHours += durationHours * assignedCount;
      point.publishedHours += slot.is_published ? durationHours * slot.guards_required : 0;
      point.openPositions += getOpenPositions(slot);
    });

    executions.forEach((execution) => {
      const point = capacityLookup.get(execution.planned_slot_date);
      if (point) {
        point.executedHours += getExecutionWorkedHours(execution, now);
      }

      const startPoint = startsLookup.get(execution.planned_slot_date);
      if (startPoint && execution.actual_clock_in_at) {
        if ((execution.clock_in_late_by_seconds ?? 0) > 0) startPoint.lateStarts += 1;
        else startPoint.onTimeStarts += 1;
      }
    });

    unstartedDueShifts.forEach((shift) => {
      const point = startsLookup.get(shift.slot_date);
      if (point) point.unstartedShifts += 1;
    });

    const dutySegments = segments.filter((segment) => segment.planned_segment_type === 'duty');
    dutySegments.forEach((segment) => {
      const key = formatDateKey(toDate(segment.planned_start_at) ?? parseDateKey(todayDate));
      const point = completionLookup.get(key);
      if (!point) return;
      if (segment.status === 'completed') point.completedSegments += 1;
      else if (segment.status === 'missed') point.missedSegments += 1;
      else if (segment.status === 'skipped') point.skippedSegments += 1;
    });

    const totalPlannedDutyHours = sum(capacitySeries.map((point) => point.plannedHours));
    const totalStaffedDutyHours = sum(capacitySeries.map((point) => point.staffedHours));
    const totalExecutedHours = sum(capacitySeries.map((point) => point.executedHours));
    const totalPublishedDutyHours = sum(capacitySeries.map((point) => point.publishedHours));
    const upcomingOpenPositions = sum(futureDutySlots.map(getOpenPositions));

    const lateStarts = executions.filter((execution) => (execution.clock_in_late_by_seconds ?? 0) > 0).length;
    const completedDutySegments = dutySegments.filter((segment) => segment.status === 'completed').length;
    const missedDutySegments = dutySegments.filter((segment) => segment.status === 'missed').length;
    const skippedDutySegments = dutySegments.filter((segment) => segment.status === 'skipped').length;

    let checkpointCompleted = 0;
    let checkpointPlanned = 0;

    dutySegments
      .filter((segment) => Boolean(segment.patrol_id))
      .forEach((segment) => {
        const plannedCount = requiredCheckpointCountByPatrolId.get(segment.patrol_id ?? '') ?? 0;
        const completions = checkpointRowsBySegmentId.get(segment.id) ?? [];
        checkpointCompleted += completions.filter((row) => row.status === 'completed').length;
        checkpointPlanned += plannedCount || completions.length;
      });

    const geofenceSamples = sum(locationSummaryRows.map((row) => Number(row.geofence_samples)));
    const withinGeofenceSamples = sum(locationSummaryRows.map((row) => Number(row.within_geofence_samples)));
    const geofenceCompliance = percentage(withinGeofenceSamples, geofenceSamples);
    const flaggedLocations = sum(locationSummaryRows.map((row) => Number(row.flagged_locations)));

    const fillRate = percentage(totalStaffedDutyHours, totalPlannedDutyHours);
    const publicationRate = percentage(totalPublishedDutyHours, totalPlannedDutyHours);
    const startRate = percentage(startedDueShifts.length, duePublishedShifts.length);
    const punctualityRate = percentage(executions.length - lateStarts, executions.length);
    const dutyCompletionRate = percentage(
      completedDutySegments,
      completedDutySegments + missedDutySegments + skippedDutySegments,
    );
    const checkpointCompletionRate = percentage(checkpointCompleted, checkpointPlanned);

    const submittedReports = openReports.filter((report) => report.status === 'submitted').length;
    const inReviewReports = openReports.filter((report) => report.status === 'in_review').length;
    const needsMoreInfoReports = openReports.filter((report) => report.status === 'needs_more_info').length;
    const escalatedReports = openReports.filter((report) => report.status === 'escalated').length;
    const closedReportsInRange = reportsInRange.filter((report) => report.status === 'closed');

    const reportAssignmentRate = percentage(
      openReports.filter((report) => Boolean(report.assigned_to)).length,
      openReports.length,
    );
    const averageOpenReportAgeHours = average(
      openReports.map((report) => hoursBetween(report.created_at, now.toISOString())),
    );
    const averageReportCloseHours = average(
      closedReportsInRange.map((report) => hoursBetween(report.created_at, report.status_updated_at ?? report.created_at)),
    );
    const averageAlarmResolveMinutes = average(
      alarmsInRange
        .filter((alarm) => Boolean(alarm.resolved_at))
        .map((alarm) => minutesBetween(alarm.triggered_at, alarm.resolved_at)),
    );

    const incidentSummary: AnalyticsIncidentSummary = {
      openReports: openReports.length,
      submittedReports,
      inReviewReports,
      needsMoreInfoReports,
      escalatedReports,
      activeAlarms: activeAlarms.length,
      averageOpenReportAgeHours,
      averageReportCloseHours,
      averageAlarmResolveMinutes,
      statusMix: [
        { key: 'submitted', label: REPORT_STATUS_LABELS.submitted, count: submittedReports, tone: 'info' },
        { key: 'in_review', label: REPORT_STATUS_LABELS.in_review, count: inReviewReports, tone: 'warning' },
        { key: 'needs_more_info', label: REPORT_STATUS_LABELS.needs_more_info, count: needsMoreInfoReports, tone: 'warning' },
        { key: 'escalated', label: REPORT_STATUS_LABELS.escalated, count: escalatedReports, tone: 'danger' },
        { key: 'alarms', label: 'Active Alarms', count: activeAlarms.length, tone: 'danger' },
      ],
    };

    const activeExecutions = executions.filter((execution) => execution.status === 'active' && !execution.actual_clock_out_at);
    const activeSites = new Set(activeExecutions.map((execution) => execution.site_id).filter(Boolean)).size;
    const overrunningExecutions = activeExecutions.filter((execution) => {
      const plannedEnd = toDate(execution.planned_end_at);
      return Boolean(plannedEnd && plannedEnd < now);
    }).length;
    const activeSiteDepartures = departureRows.filter((row) => !row.returned_at).length;

    const liveSummary: AnalyticsLiveSummary = {
      activeExecutions: activeExecutions.length,
      activeSites,
      overrunningExecutions,
      liveTrackedGuards: liveTrackedGuardIds.size,
      lowBatteryCount,
      activeSiteDepartures,
      manualInterventions: interventionRows.length,
    };

    const dutyMixMap = new Map<string, AnalyticsDutyMixItem>();
    Object.entries(DUTY_KIND_LABELS).forEach(([key, label]) => {
      dutyMixMap.set(key, { key, label, plannedHours: 0, staffedHours: 0, slotCount: 0, fillRate: null });
    });
    rangeDutySlots.forEach((slot) => {
      if (!dutyMixMap.has(slot.duty_kind)) {
        dutyMixMap.set(slot.duty_kind, {
          key: slot.duty_kind,
          label: getDutyKindLabel(slot.duty_kind),
          plannedHours: 0,
          staffedHours: 0,
          slotCount: 0,
          fillRate: null,
        });
      }
      const item = dutyMixMap.get(slot.duty_kind)!;
      const durationHours = getDutyDurationHours(slot);
      item.plannedHours += durationHours * slot.guards_required;
      item.staffedHours += durationHours * getAssignedCount(slot);
      item.slotCount += 1;
    });
    const dutyMix = [...dutyMixMap.values()]
      .map((item) => ({
        ...item,
        fillRate: percentage(item.staffedHours, item.plannedHours),
      }))
      .filter((item) => item.slotCount > 0)
      .sort((left, right) => right.plannedHours - left.plannedHours);

    const heatmapBands = [
      { key: '00-04', label: '00-04', startMinute: 0, endMinute: 240 },
      { key: '04-08', label: '04-08', startMinute: 240, endMinute: 480 },
      { key: '08-12', label: '08-12', startMinute: 480, endMinute: 720 },
      { key: '12-16', label: '12-16', startMinute: 720, endMinute: 960 },
      { key: '16-20', label: '16-20', startMinute: 960, endMinute: 1200 },
      { key: '20-24', label: '20-24', startMinute: 1200, endMinute: 1440 },
    ];
    const weekdayOrder = [1, 2, 3, 4, 5, 6, 0];
    const weekdayLabels = weekdayOrder.reduce<Record<number, string>>((labels, weekday) => {
      labels[weekday] = new Date(2024, 0, weekday === 0 ? 7 : weekday).toLocaleDateString(getLocale(), {
        weekday: 'short',
      });
      return labels;
    }, {});
    const weekdayOccurrences = new Map<number, number>();
    weekdayOrder.forEach((weekday) => weekdayOccurrences.set(weekday, 0));
    descriptors.forEach((descriptor) => {
      const weekday = parseDateKey(descriptor.key).getDay();
      weekdayOccurrences.set(weekday, (weekdayOccurrences.get(weekday) ?? 0) + 1);
    });

    const heatmapHours = new Map<string, number>();
    weekdayOrder.forEach((weekday) => {
      heatmapBands.forEach((band) => {
        heatmapHours.set(`${weekday}:${band.key}`, 0);
      });
    });

    rangeDutySlots.forEach((slot) => {
      const baseDate = parseDateKey(slot.slot_date);
      heatmapBands.forEach((band) => {
        for (let dayOffset = 0; dayOffset < 2; dayOffset += 1) {
          const bandStart = band.startMinute + (dayOffset * 1440);
          const bandEnd = band.endMinute + (dayOffset * 1440);
          const overlap = Math.max(0, Math.min(slot.end_minute, bandEnd) - Math.max(slot.start_minute, bandStart));
          if (!overlap) continue;
          const targetDate = addDays(baseDate, dayOffset);
          const weekday = targetDate.getDay();
          const key = `${weekday}:${band.key}`;
          const hours = (overlap / 60) * slot.guards_required;
          heatmapHours.set(key, (heatmapHours.get(key) ?? 0) + hours);
        }
      });
    });

    const heatmapRows = weekdayOrder.map((weekday) => {
      const dayCount = weekdayOccurrences.get(weekday) ?? 1;
      return {
        key: String(weekday),
        label: weekdayLabels[weekday],
        cells: heatmapBands.map((band) => ({
          key: `${weekday}:${band.key}`,
          label: band.label,
          hours: (heatmapHours.get(`${weekday}:${band.key}`) ?? 0) / dayCount,
          intensity: 0,
        })),
      };
    });
    const heatmapMax = Math.max(
      ...heatmapRows.flatMap((row) => row.cells.map((cell) => cell.hours)),
      0,
    );
    heatmapRows.forEach((row) => {
      row.cells.forEach((cell) => {
        cell.intensity = heatmapMax ? cell.hours / heatmapMax : 0;
      });
    });

    const siteAccumulators = new Map<string, SiteAccumulator>();
    if (selectedSiteId) {
      siteAccumulators.set(
        selectedSiteId,
        createSiteAccumulator(selectedSiteId, getSiteName(selectedSiteId, siteMap)),
      );
    } else {
      sites.forEach((site) => {
        siteAccumulators.set(
          site.id,
          createSiteAccumulator(site.id, site.name?.trim() || 'Unknown site'),
        );
      });
    }

    rangeDutySlots.forEach((slot) => {
      const site = ensureSiteAccumulator(siteAccumulators, slot.site_id, siteMap);
      site.plannedHours += getDutyDurationHours(slot) * slot.guards_required;
    });
    futureDutySlots.forEach((slot) => {
      const site = ensureSiteAccumulator(siteAccumulators, slot.site_id, siteMap);
      site.upcomingRequiredPositions += slot.guards_required;
      site.upcomingAssignedPositions += getAssignedCount(slot);
      site.upcomingPublishedPositions += slot.is_published ? slot.guards_required : 0;
      site.upcomingOpenPositions += getOpenPositions(slot);
    });
    executions.forEach((execution) => {
      const site = ensureSiteAccumulator(siteAccumulators, execution.site_id, siteMap);
      site.executedHours += getExecutionWorkedHours(execution, now);
      if ((execution.clock_in_late_by_seconds ?? 0) > 0) site.lateStarts += 1;
      if (execution.status === 'active' && !execution.actual_clock_out_at) site.activeExecutions += 1;
    });
    dutySegments.forEach((segment) => {
      if (segment.status === 'missed' || segment.status === 'skipped') {
        const site = ensureSiteAccumulator(siteAccumulators, segment.site_id, siteMap);
        site.missedSegments += 1;
      }
    });
    openReports.forEach((report) => {
      const site = ensureSiteAccumulator(siteAccumulators, report.site_id, siteMap);
      site.openReports += 1;
    });
    activeAlarms.forEach((alarm) => {
      const site = ensureSiteAccumulator(siteAccumulators, alarm.site_id, siteMap);
      site.activeAlarms += 1;
    });
    locationSummaryRows.forEach((row) => {
      const site = ensureSiteAccumulator(siteAccumulators, row.site_id, siteMap);
      site.flaggedLocations += Number(row.flagged_locations);
    });
    interventionRows.forEach((row) => {
      const site = ensureSiteAccumulator(siteAccumulators, row.site_id, siteMap);
      site.manualInterventions += 1;
    });

    const riskBoard = [...siteAccumulators.values()]
      .map((site) => {
        const upcomingFillRate = percentage(site.upcomingAssignedPositions, site.upcomingRequiredPositions);
        const publicationRateValue = percentage(site.upcomingPublishedPositions, site.upcomingRequiredPositions);
        const riskScore = (
          (site.upcomingOpenPositions * 4)
          + (site.activeAlarms * 5)
          + (site.openReports * 3)
          + (site.missedSegments * 2)
          + site.flaggedLocations
          + site.manualInterventions
          + ((upcomingFillRate !== null && upcomingFillRate < 85) ? 2 : 0)
          + ((publicationRateValue !== null && publicationRateValue < 85) ? 1 : 0)
        );

        return {
          siteId: site.siteId,
          siteName: site.siteName,
          plannedHours: site.plannedHours,
          executedHours: site.executedHours,
          upcomingFillRate,
          publicationRate: publicationRateValue,
          upcomingOpenPositions: site.upcomingOpenPositions,
          lateStarts: site.lateStarts,
          missedSegments: site.missedSegments,
          openReports: site.openReports,
          activeAlarms: site.activeAlarms,
          flaggedLocations: site.flaggedLocations,
          manualInterventions: site.manualInterventions,
          activeExecutions: site.activeExecutions,
          riskScore,
          riskLabel: getRiskLabel(riskScore),
          riskTone: getRiskTone(riskScore),
        } satisfies AnalyticsSiteRiskRow;
      })
      .filter((row) => (
        selectedSiteId
          ? row.siteId === selectedSiteId
          : (
            row.plannedHours > 0
            || row.upcomingOpenPositions > 0
            || row.openReports > 0
            || row.activeAlarms > 0
            || row.missedSegments > 0
            || row.flaggedLocations > 0
          )
      ))
      .sort((left, right) => {
        if (right.riskScore !== left.riskScore) return right.riskScore - left.riskScore;
        if (right.upcomingOpenPositions !== left.upcomingOpenPositions) return right.upcomingOpenPositions - left.upcomingOpenPositions;
        return left.siteName.localeCompare(right.siteName, undefined, { sensitivity: 'base' });
      });

    const guardAccumulators = new Map<string, GuardAccumulator>();
    guards.forEach((guard) => {
      guardAccumulators.set(
        guard.id,
        createGuardAccumulator(guard.id, guard.full_name?.trim() || 'Unknown guard'),
      );
    });

    shifts.forEach((shift) => {
      const guard = ensureGuardAccumulator(guardAccumulators, shift.guard_id, profileMap);
      if (!guard) return;
      guard.plannedHours += getShiftDurationHours(shift);
      if (shift.is_published && isShiftDue(shift, siteMap.get(shift.site_id), now)) {
        guard.dueShifts += 1;
      }
      if (shift.site_id) guard.siteIds.add(shift.site_id);
    });

    executions.forEach((execution) => {
      const guard = ensureGuardAccumulator(guardAccumulators, execution.guard_id, profileMap);
      if (!guard) return;
      guard.executedHours += getExecutionWorkedHours(execution, now);
      guard.startedShifts += execution.actual_clock_in_at ? 1 : 0;
      if ((execution.clock_in_late_by_seconds ?? 0) > 0) guard.lateStarts += 1;
      if (execution.site_id) guard.siteIds.add(execution.site_id);
      if (execution.status === 'active' && !execution.actual_clock_out_at) guard.activeExecution = true;
    });

    dutySegments.forEach((segment) => {
      const guard = ensureGuardAccumulator(guardAccumulators, segment.guard_id, profileMap);
      if (!guard) return;
      if (segment.status === 'completed') guard.completedSegments += 1;
      else if (segment.status === 'missed') guard.missedSegments += 1;
      else if (segment.status === 'skipped') guard.skippedSegments += 1;
      if (segment.site_id) guard.siteIds.add(segment.site_id);

      if (segment.patrol_id) {
        const plannedCount = requiredCheckpointCountByPatrolId.get(segment.patrol_id) ?? 0;
        const completions = checkpointRowsBySegmentId.get(segment.id) ?? [];
        guard.checkpointPlanned += plannedCount || completions.length;
        guard.checkpointCompleted += completions.filter((row) => row.status === 'completed').length;
      }
    });

    liveTrackedGuardIds.forEach((guardId) => {
      const guard = ensureGuardAccumulator(guardAccumulators, guardId, profileMap);
      if (guard) guard.liveTracked = true;
    });

    const guardLeaders = [...guardAccumulators.values()]
      .map((guard) => {
        const dutyCompletion = percentage(
          guard.completedSegments,
          guard.completedSegments + guard.missedSegments + guard.skippedSegments,
        );
        const checkpointCompletion = percentage(guard.checkpointCompleted, guard.checkpointPlanned);
        const punctuality = percentage(guard.startedShifts - guard.lateStarts, guard.startedShifts);
        const momentumScore = (
          guard.executedHours
          + (guard.completedSegments * 0.35)
          + (guard.checkpointCompleted * 0.08)
          + (guard.liveTracked ? 0.5 : 0)
          - (guard.missedSegments * 2)
          - (guard.skippedSegments * 1.5)
          - (guard.lateStarts * 0.5)
        );

        return {
          guardId: guard.guardId,
          fullName: guard.fullName,
          plannedHours: guard.plannedHours,
          executedHours: guard.executedHours,
          punctualityRate: punctuality,
          dutyCompletionRate: dutyCompletion,
          checkpointCompletionRate: checkpointCompletion,
          lateStarts: guard.lateStarts,
          missedSegments: guard.missedSegments + guard.skippedSegments,
          siteCount: guard.siteIds.size,
          liveTracked: guard.liveTracked,
          activeExecution: guard.activeExecution,
          momentumScore,
        } satisfies AnalyticsGuardLeader;
      })
      .filter((guard) => (
        guard.plannedHours > 0
        || guard.executedHours > 0
        || guard.liveTracked
        || guard.activeExecution
      ))
      .sort((left, right) => {
        if (right.momentumScore !== left.momentumScore) return right.momentumScore - left.momentumScore;
        return right.executedHours - left.executedHours;
      })
      .slice(0, 8);

    const upcomingGaps = futureDutySlots
      .filter((slot) => getOpenPositions(slot) > 0 || !slot.is_published)
      .map((slot) => ({
        dutyId: slot.id,
        siteId: slot.site_id,
        siteName: getSiteName(slot.site_id, siteMap),
        dutyName: slot.name || getDutyKindLabel(slot.duty_kind),
        dutyKind: slot.duty_kind,
        slotDate: slot.slot_date,
        startMinute: slot.start_minute,
        endMinute: slot.end_minute,
        guardsRequired: slot.guards_required,
        assignedCount: getAssignedCount(slot),
        openPositions: getOpenPositions(slot),
        isPublished: slot.is_published,
        requiredLicenceTypeId: slot.required_licence_type_id,
      } satisfies AnalyticsUpcomingGap))
      .sort((left, right) => {
        if (left.slotDate !== right.slotDate) return left.slotDate.localeCompare(right.slotDate);
        if (left.startMinute !== right.startMinute) return left.startMinute - right.startMinute;
        return right.openPositions - left.openPositions;
      })
      .slice(0, 12);

    const timelineItems: AnalyticsTimelineItem[] = [];

    executions.forEach((execution) => {
      if (execution.actual_clock_in_at && execution.actual_clock_in_at >= recentTimelineStart.toISOString()) {
        const lateMinutes = Math.round((execution.clock_in_late_by_seconds ?? 0) / 60);
        timelineItems.push({
          id: `clock-in-${execution.id}`,
          occurredAt: execution.actual_clock_in_at,
          kind: 'execution',
          tone: lateMinutes > 0 ? 'warning' : 'success',
          title: lateMinutes > 0 ? `${getGuardName(execution.guard_id, profileMap)} clocked in late` : `${getGuardName(execution.guard_id, profileMap)} clocked in`,
          detail: `${getSiteName(execution.site_id, siteMap)}${lateMinutes > 0 ? ` • ${lateMinutes}m late` : ' • On time'}`,
          siteName: getSiteName(execution.site_id, siteMap),
          guardName: getGuardName(execution.guard_id, profileMap),
        });
      }

      if (execution.actual_clock_out_at && execution.actual_clock_out_at >= recentTimelineStart.toISOString()) {
        timelineItems.push({
          id: `clock-out-${execution.id}`,
          occurredAt: execution.actual_clock_out_at,
          kind: 'execution',
          tone: 'neutral',
          title: `${getGuardName(execution.guard_id, profileMap)} clocked out`,
          detail: `${getSiteName(execution.site_id, siteMap)} • ${getExecutionWorkedHours(execution, now).toFixed(1)}h worked`,
          siteName: getSiteName(execution.site_id, siteMap),
          guardName: getGuardName(execution.guard_id, profileMap),
        });
      }
    });

    dutySegments
      .filter((segment) => segment.status === 'missed' || segment.status === 'skipped')
      .forEach((segment) => {
        const eventTime = segment.actual_ended_at ?? segment.planned_end_at;
        if (eventTime < recentTimelineStart.toISOString()) return;
        timelineItems.push({
          id: `segment-${segment.id}`,
          occurredAt: eventTime,
          kind: 'segment',
          tone: 'danger',
          title: `${segment.planned_label} ${segment.status === 'missed' ? 'missed' : 'skipped'}`,
          detail: `${getGuardName(segment.guard_id, profileMap)} • ${getSiteName(segment.site_id, siteMap)}`,
          siteName: getSiteName(segment.site_id, siteMap),
          guardName: getGuardName(segment.guard_id, profileMap),
        });
      });

    checkpointRows.forEach((row) => {
      if (row.recorded_at < recentTimelineStart.toISOString()) return;
      const isLateCompletion = row.status === 'completed' && (row.completed_late_by_seconds ?? 0) > 600;
      if (row.status === 'completed' && !isLateCompletion) return;
      timelineItems.push({
        id: `checkpoint-${row.id}`,
        occurredAt: row.recorded_at,
        kind: 'checkpoint',
        tone: row.status === 'completed' ? 'warning' : 'danger',
        title: row.status === 'completed' ? 'Checkpoint completed late' : `Checkpoint ${row.status}`,
        detail: `${getGuardName(row.guard_id, profileMap)} • ${getSiteName(row.site_id, siteMap)}`,
        siteName: getSiteName(row.site_id, siteMap),
        guardName: getGuardName(row.guard_id, profileMap),
      });
    });

    reportsInRange.forEach((report) => {
      if (report.created_at < recentTimelineStart.toISOString()) return;
      timelineItems.push({
        id: `report-${report.id}`,
        occurredAt: report.created_at,
        kind: 'report',
        tone: report.status === 'escalated' ? 'danger' : 'info',
        title: report.status === 'escalated' ? 'Report escalated' : 'Report filed',
        detail: `${getSiteName(report.site_id, siteMap)} • ${getReportTitle(report)}`,
        siteName: getSiteName(report.site_id, siteMap),
        guardName: getGuardName(report.guard_id, profileMap),
      });
    });

    alarmsInRange.forEach((alarm) => {
      if (alarm.triggered_at < recentTimelineStart.toISOString()) return;
      timelineItems.push({
        id: `alarm-${alarm.id}`,
        occurredAt: alarm.triggered_at,
        kind: 'alarm',
        tone: 'danger',
        title: 'Alarm raised',
        detail: `${getSiteName(alarm.site_id, siteMap)} • ${alarm.title}`,
        siteName: getSiteName(alarm.site_id, siteMap),
        guardName: getGuardName(alarm.triggered_by, profileMap),
      });
    });

    interventionRows.forEach((row) => {
      if (row.intervention_at < recentTimelineStart.toISOString()) return;
      timelineItems.push({
        id: `intervention-${row.id}`,
        occurredAt: row.intervention_at,
        kind: 'intervention',
        tone: 'warning',
        title: row.summary || humanizeKey(row.intervention_type),
        detail: `${getGuardName(row.guard_id, profileMap)} • ${getSiteName(row.site_id, siteMap)}`,
        siteName: getSiteName(row.site_id, siteMap),
        guardName: getGuardName(row.guard_id, profileMap),
      });
    });

    departureRows.forEach((row) => {
      if (row.departed_at >= recentTimelineStart.toISOString()) {
        timelineItems.push({
          id: `departure-start-${row.id}`,
          occurredAt: row.departed_at,
          kind: 'departure',
          tone: row.departure_type === 'authorised' ? 'warning' : 'danger',
          title: row.departure_type === 'authorised' ? 'Authorised departure logged' : 'Guard left site',
          detail: `${getGuardName(row.guard_id, profileMap)} • ${getSiteName(row.site_id, siteMap)}`,
          siteName: getSiteName(row.site_id, siteMap),
          guardName: getGuardName(row.guard_id, profileMap),
        });
      }
      if (row.returned_at && row.returned_at >= recentTimelineStart.toISOString()) {
        timelineItems.push({
          id: `departure-return-${row.id}`,
          occurredAt: row.returned_at,
          kind: 'departure',
          tone: 'success',
          title: 'Guard returned to site',
          detail: `${getGuardName(row.guard_id, profileMap)} • ${getSiteName(row.site_id, siteMap)}`,
          siteName: getSiteName(row.site_id, siteMap),
          guardName: getGuardName(row.guard_id, profileMap),
        });
      }
    });

    const timeline = timelineItems
      .sort((left, right) => new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime())
      .slice(0, 12);

    const warnings = selectedSiteId && !siteMap.has(selectedSiteId)
      ? ['The selected site is no longer available in this company.']
      : [];

    const dashboard: AnalyticsDashboard = {
      companyName: company.name?.trim() || 'Analytics',
      generatedAt: now.toISOString(),
      rangeDays,
      rangeLabel: getRangeLabel(rangeDays),
      selectedSiteId,
      selectedSiteName: selectedSiteId ? getSiteName(selectedSiteId, siteMap) : null,
      availableSites,
      warnings,
      headline: {
        plannedDutyHours: totalPlannedDutyHours,
        staffedDutyHours: totalStaffedDutyHours,
        executedHours: totalExecutedHours,
        duePublishedShifts: duePublishedShifts.length,
        startedShifts: startedDueShifts.length,
        lateStarts,
        unstartedPublishedShifts: unstartedDueShifts.length,
        completedDutySegments,
        missedDutySegments,
        skippedDutySegments,
        upcomingOpenPositions,
        checkpointCompleted,
        checkpointPlanned,
        flaggedLocations,
      },
      quality: {
        fillRate,
        publicationRate,
        startRate,
        punctualityRate,
        dutyCompletionRate,
        checkpointCompletionRate,
        geofenceCompliance,
        reportAssignmentRate,
      },
      incidents: incidentSummary,
      live: liveSummary,
      charts: {
        capacity: capacitySeries,
        starts: startsSeries,
        completions: completionSeries,
        dutyMix,
        heatmap: {
          columns: heatmapBands.map((band) => band.label),
          rows: heatmapRows,
          maxHours: heatmapMax,
        },
      },
      riskBoard,
      guardLeaders,
      upcomingGaps,
      timeline,
    };
    dashboardCache.set(cacheKey, {
      expiresAt: Date.now() + ANALYTICS_CACHE_TTL_MS,
      dashboard,
    });
    return dashboard;
  },
};
